import { type Carer, UserRole } from '@lmc/ts-common'
import { Icon, theme } from '@lmc/ui-kit'
import {
  Button,
  Heading,
  Text,
  type TextLevel,
  type TextProps
} from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import GeoLocation from 'components/GeoLocation/GeoLocation'
import ClockIn from 'components/icons/ClockIn'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { type FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import type {
  ClockInOutAction,
  ClockInOutType
} from 'screens/Rostering/hooks/useClockInOut'
import { WarningIcon } from 'screens/Rostering/RotaBuilder/common/components/icons'
import {
  GEOLOCATION_MESSAGE_KEYS,
  GEOLOCATION_WARNING_STATUSES,
  getTimeWarnings
} from 'screens/Rostering/RotaDrawer/components/DrawerStatus/utils'
import { Warnings } from 'screens/Rostering/RotaDrawer/components/DrawerStatus/Warnings'
import {
  type ClockInLocation,
  GeolocationStatus
} from 'screens/Rostering/types'
import styled from 'styled-components'
import { getDateTimeInZone, TIME_FIRST_DATE_FORMAT } from '../utils.datetime'

dayjs.extend(isoWeek)

type ClockInOutDetailsBaseProps = {
  time?: string
  note?: string
  location?: ClockInLocation
  type: ClockInOutType
  action: ClockInOutAction
  warnings?: string[]
  openClockInOutModal: () => void
  isDraft?: boolean
  isDeletedSU?: boolean
  timezone?: string
  publishedRota?: boolean
  showInvalidAddressForGeofencingWarning?: boolean
  geolocationStatus?: GeolocationStatus
}

export const ClockInOutDetailsBase: FC<ClockInOutDetailsBaseProps> = ({
  time,
  note,
  location,
  type,
  action,
  warnings,
  openClockInOutModal,
  isDraft,
  isDeletedSU,
  timezone,
  publishedRota,
  showInvalidAddressForGeofencingWarning,
  geolocationStatus
}) => {
  const { t } = useTranslation()

  const userRole = useSelector(
    (state: { global: { user: Carer } }) => state.global.user?.role
  )

  const isCareHomeAdmin =
    userRole === UserRole.careHomeAdmin || userRole === UserRole.management

  const canEdit = isCareHomeAdmin && !isDraft && !isDeletedSU && !publishedRota

  const actionIcon = useMemo(() => {
    return action === 'edit' ? (
      <Icon name="EditOutlined" />
    ) : (
      <ClockIn size="16" />
    )
  }, [action])

  return (
    <GrayWrapper>
      <StyledHeading level={4}>
        {t(`CLOCK_${type.toUpperCase()}_DETAILS`)}

        {canEdit && (
          <GrayTextButton
            variant="text"
            icon={actionIcon}
            onClick={openClockInOutModal}
          >
            {t(`${action.toUpperCase()}_CLOCK_${type.toUpperCase()}`)}
          </GrayTextButton>
        )}
      </StyledHeading>

      <Divider />

      <GrayWrapperSection>
        <GrayText level={2}>{t(`CLOCK_${type.toUpperCase()}_TIME`)}</GrayText>
        <AlignedText>
          <Warnings warnings={getTimeWarnings(warnings)}>
            {time
              ? dayjs(time).tz(timezone).format(TIME_FIRST_DATE_FORMAT)
              : t('NOT_AVAILABLE')}
          </Warnings>
        </AlignedText>
      </GrayWrapperSection>

      {!!note && (
        <GrayWrapperSection>
          <GrayText level={2}>{t('NOTE')}</GrayText>
          <Text>{note}</Text>
        </GrayWrapperSection>
      )}

      <GrayWrapperSection data-testid="location-section">
        <LocationTitle level={2}>{t('LOCATION')}</LocationTitle>
        {showInvalidAddressForGeofencingWarning && time && (
          <InvalidAddressWarningRow>
            <WarningIcon />
            <Text>
              {t(
                type === 'in'
                  ? 'CLOCKED_IN_WITH_INVALID_VISIT_ADDRESS'
                  : 'CLOCKED_OUT_WITH_INVALID_VISIT_ADDRESS'
              )}
            </Text>
          </InvalidAddressWarningRow>
        )}
        {time &&
          geolocationStatus &&
          GEOLOCATION_WARNING_STATUSES.includes(geolocationStatus) && (
            <InvalidAddressWarningRow>
              {geolocationStatus !== GeolocationStatus.ADMIN_OVERRIDE && (
                <WarningIcon />
              )}
              <Text>
                {t(GEOLOCATION_MESSAGE_KEYS[geolocationStatus][type])}
              </Text>
            </InvalidAddressWarningRow>
          )}
        <LocationWrapper>
          {location && !isDeletedSU && (
            <GeoLocation
              longitude={location.lng}
              latitude={location.lat}
              accuracy={location.accuracy}
            />
          )}
          {(!location || isDeletedSU) && (
            <span>
              {t('GEO_LOCATION')}:{' '}
              <GrayText weight="medium" color={theme.color.neutral.color4}>
                {isDeletedSU ? t('ANONYMISED') : t('NOT_AVAILABLE')}
              </GrayText>
            </span>
          )}
        </LocationWrapper>
      </GrayWrapperSection>
    </GrayWrapper>
  )
}

const StyledHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
`

type GrayTextProps = TextProps<TextLevel> & { color?: string }

const GrayText = styled(Text)<GrayTextProps>`
  color: ${(props) => props.color || props.theme.color.neutral.color5};
`

const GrayTextButton = styled(Button)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const AlignedText = styled(Text)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

const GrayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background-color: ${(props) => props.theme.color.neutral.color10};
  border-radius: 20px;
`

const GrayWrapperSection = styled.div`
  display: flex;
  flex-direction: column;
`

const LocationWrapper = styled.div`
  width: 100%;
  display: grid;
  gap: 12px;
`

const LocationTitle = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
  margin-bottom: 6px;
`

const InvalidAddressWarningRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
